/**
 * useDesktopGuard — Redirect desktop browsers to the QR handoff screen.
 *
 * Runs once after config is loaded. If:
 *   - detectLayout() === 'desktop', AND
 *   - config.brand.allowDesktop === false (or undefined, which defaults to false)
 *
 * …then the app screen is set to 'qr', showing the QR handoff instead of
 * the start screen or viewer.
 *
 * This hook is the single place where the desktop-vs-mobile routing decision
 * is made. It must be called in App.tsx after config is available.
 *
 * @see docs/ims-brand-frontends/03-design-system.md#qr-screen-logik
 * @see docs/ims-brand-frontends/08-ui-screens.md#screen-1--qr-screen-desktop-fallback
 */
import { useEffect } from 'react';
import type { ResolvedConfig } from '@ims/shared';
import { useAppStore } from '../store/app.store';
import { detectLayout } from '../utils/detectLayout';

interface UseDesktopGuardOptions {
  config: ResolvedConfig | null;
  skip?: boolean;
}

export function useDesktopGuard({ config, skip }: UseDesktopGuardOptions): void {
  const setScreen = useAppStore((s) => s.setScreen);
  const screen = useAppStore((s) => s.screen);

  useEffect(() => {
    if (skip) return;

    // Wait until config is resolved — we need brand.allowDesktop
    if (!config) return;

    // Only evaluate once: if we're already on the QR screen don't re-trigger
    if (screen === 'qr') return;

    const isDesktop = detectLayout() === 'desktop';
    // allowDesktop defaults to false when not explicitly set to true
    const allowDesktop = config.brand.allowDesktop ?? false;

    if (isDesktop && !allowDesktop) {
      setScreen('qr');
    }
  }, [config, screen, setScreen, skip]);
}
